import { db } from '../firebase';
import { collection, query, where, getDocs, addDoc, updateDoc, deleteDoc, doc, getDoc } from 'firebase/firestore';

export const getSchedules = async (params = {}) => {
    try {
        console.log('getSchedules params:', params);
        const schedulesRef = collection(db, 'schedules');
        let q = schedulesRef;
        
        // Tworzenie filtrów zapytania
        const conditions = [];
        
        // Dodaj filtr użytkownika jeśli określony
        if (params.user_id) {
            conditions.push(where('user_id', '==', params.user_id));
        }
        
        // Jeżeli podany jest rok i miesiąc, oblicz zakres dat
        if (params.year && params.month) {
            const year = params.year;
            const month = params.month;
            
            // Sformatuj datę początku miesiąca (YYYY-MM-01)
            const startDate = `${year}-${String(month).padStart(2, '0')}-01`;
            
            // Oblicz ostatni dzień miesiąca i sformatuj datę końca (YYYY-MM-DD)
            const lastDay = new Date(year, month, 0).getDate(); // 0 jako dzień da nam ostatni dzień poprzedniego miesiąca
            const endDate = `${year}-${String(month).padStart(2, '0')}-${String(lastDay).padStart(2, '0')}`;
            
            console.log(`Zakres dat: ${startDate} - ${endDate}`);
            
            // Dodaj filtry zakresu dat
            conditions.push(where('date', '>=', startDate));
            conditions.push(where('date', '<=', endDate));
        } 
        // Jeżeli podane są konkretne daty startDate i endDate
        else if (params.startDate && params.endDate) {
            conditions.push(where('date', '>=', params.startDate));
            conditions.push(where('date', '<=', params.endDate));
        }
        
        // Zastosuj wszystkie warunki w zapytaniu
        if (conditions.length > 0) {
            q = query(schedulesRef, ...conditions);
        }

        const snapshot = await getDocs(q);
        const schedules = [];

        // Przygotuj podstawowe dane bez dodatkowych zapytań
        for (const scheduleDoc of snapshot.docs) {
            const scheduleData = scheduleDoc.data();
            
            schedules.push({
                id: scheduleDoc.id,
                ...scheduleData,
                user_name: scheduleData.user_name || 'Brak nazwy'
            });
        }
        
        return schedules;
    } catch (error) {
        console.error('Error getting schedules:', error);
        throw error;
    }
};

export const createSchedule = async (scheduleData) => {
    try {
        // Sprawdź czy użytkownik który tworzy zmianę jest adminem
        const creatorDoc = await getDoc(doc(db, 'users', scheduleData.created_by_id));
        const creatorData = creatorDoc.data();
        const isAdmin = creatorData?.role === 'admin';

        // Pobierz dane użytkownika dla którego tworzona jest zmiana
        const userDoc = await getDoc(doc(db, 'users', scheduleData.user_id));
        const userData = userDoc.data();
        
        // Przygotuj pełną nazwę użytkownika
        const userName = userData ? `${userData.firstName || ''} ${userData.lastName || ''}`.trim() : 'Brak nazwy';
        
        // Przygotuj dane do zapisu
        const scheduleToSave = {
            ...scheduleData,
            user_name: userName,
            status: isAdmin ? 'approved' : 'pending', // Jeśli admin tworzy, od razu zatwierdzone
            createdAt: new Date().toISOString(),
            approved_by_id: isAdmin ? scheduleData.created_by_id : null // Jeśli admin tworzy, on zatwierdza
        };
        
        // Jeśli admin zatwierdza, zapisujemy również jego nazwę
        if (isAdmin) {
            scheduleToSave.approved_by_name = `${creatorData.firstName || ''} ${creatorData.lastName || ''}`.trim();
        }

        const docRef = await addDoc(collection(db, 'schedules'), scheduleToSave);

        return {
            id: docRef.id,
            ...scheduleToSave
        };
    } catch (error) {
        console.error('Error creating schedule:', error);
        throw error;
    }
};

export const updateSchedule = async (scheduleId, scheduleData) => {
    try {
        const scheduleRef = doc(db, 'schedules', scheduleId);
        await updateDoc(scheduleRef, {
            ...scheduleData,
            updatedAt: new Date().toISOString()
        });
        return {
            id: scheduleId,
            ...scheduleData
        };
    } catch (error) {
        console.error('Error updating schedule:', error);
        throw error;
    }
};

export const deleteSchedule = async (scheduleId) => {
    try {
        await deleteDoc(doc(db, 'schedules', scheduleId));
        return { id: scheduleId };
    } catch (error) {
        console.error('Error deleting schedule:', error);
        throw error;
    }
};

export const approveSchedule = async (scheduleId) => {
    try {
        const scheduleRef = doc(db, 'schedules', scheduleId);
        await updateDoc(scheduleRef, {
            status: 'approved',
            updatedAt: new Date().toISOString()
        });
        return { id: scheduleId, status: 'approved' };
    } catch (error) {
        console.error('Error approving schedule:', error);
        throw error;
    }
};

export const rejectSchedule = async (scheduleId) => {
    try {
        const scheduleRef = doc(db, 'schedules', scheduleId);
        await updateDoc(scheduleRef, {
            status: 'rejected',
            updatedAt: new Date().toISOString()
        });
        return { id: scheduleId, status: 'rejected' };
    } catch (error) {
        console.error('Error rejecting schedule:', error);
        throw error;
    }
};

export const getMonthSummary = async (year, month, location = null) => {
    try {
        const startDate = new Date(year, month - 1, 1).toISOString();
        const endDate = new Date(year, month, 0).toISOString();

        const schedulesRef = collection(db, 'schedules');
        let q = query(schedulesRef,
            where('date', '>=', startDate),
            where('date', '<=', endDate)
        );

        if (location) {
            q = query(q, where('locationId', '==', location));
        }

        const snapshot = await getDocs(q);
        const schedules = snapshot.docs.map(doc => {
            const data = doc.data();
            // Oblicz czas trwania zmiany w minutach
            const startTime = data.start_time.split(':');
            const endTime = data.end_time.split(':');
            const startMinutes = parseInt(startTime[0]) * 60 + parseInt(startTime[1]);
            const endMinutes = parseInt(endTime[0]) * 60 + parseInt(endTime[1]);
            const duration = endMinutes - startMinutes;

            return {
                id: doc.id,
                ...data,
                duration: duration // Dodaj obliczony czas trwania
            };
        });

        // Oblicz podsumowanie
        const summary = {
            total_shifts: schedules.length,
            total_minutes: schedules.reduce((acc, schedule) => acc + schedule.duration, 0),
            user_summaries: []
        };

        // Grupuj po użytkownikach
        const userGroups = schedules.reduce((groups, schedule) => {
            const userId = schedule.user_id; // Zmienione z userId
            if (!groups[userId]) {
                groups[userId] = {
                    user: { id: userId },
                    total_shifts: 0,
                    total_minutes: 0,
                    locations: {}
                };
            }
            groups[userId].total_shifts++;
            groups[userId].total_minutes += schedule.duration;

            if (!groups[userId].locations[schedule.location]) { // Zmienione z locationId
                groups[userId].locations[schedule.location] = { // Zmienione z locationId
                    location_id: schedule.location, // Zmienione
                    location_name: schedule.location, // Używamy nazwy lokalizacji
                    shifts_count: 0,
                    total_minutes: 0
                };
            }
            groups[userId].locations[schedule.location].shifts_count++;
            groups[userId].locations[schedule.location].total_minutes += schedule.duration;

            return groups;
        }, {});

        summary.user_summaries = Object.values(userGroups).map(group => ({
            ...group,
            locations: Object.values(group.locations)
        }));

        return summary;
    } catch (error) {
        console.error('Error getting month summary:', error);
        throw error;
    }
};

export const getEmployeeMonthDetails = async (userId, year, month, userData) => {
    try {
        // Używamy przekazanych danych użytkownika
        console.log('User data in getEmployeeMonthDetails:', userData);

        const startDate = `${year}-${String(month).padStart(2, '0')}-01`;
        const endDate = `${year}-${String(month).padStart(2, '0')}-${String(new Date(year, month, 0).getDate()).padStart(2, '0')}`;

        const schedulesRef = collection(db, 'schedules');
        const q = query(schedulesRef,
            where('user_id', '==', userId),
            where('date', '>=', startDate),
            where('date', '<=', endDate)
        );

        const snapshot = await getDocs(q);
        const shifts = snapshot.docs.map(doc => {
            const data = doc.data();
            // Oblicz czas trwania zmiany w minutach
            const [startHours, startMinutes] = data.start_time.split(':');
            const [endHours, endMinutes] = data.end_time.split(':');
            const startTotalMinutes = parseInt(startHours) * 60 + parseInt(startMinutes);
            const endTotalMinutes = parseInt(endHours) * 60 + parseInt(endMinutes);
            const duration = endTotalMinutes - startTotalMinutes;

            return {
                id: doc.id,
                ...data,
                duration_minutes: duration,
                location_name: data.location
            };
        });

        // Grupuj zmiany po dniach
        const daysInMonth = new Date(year, month, 0).getDate();
        const days = [];

        for (let day = 1; day <= daysInMonth; day++) {
            const date = `${year}-${String(month).padStart(2, '0')}-${String(day).padStart(2, '0')}`;
            const dayShifts = shifts.filter(shift => shift.date === date);
            const weekday = new Date(year, month - 1, day)
                .toLocaleDateString('pl-PL', { weekday: 'long' });

            days.push({
                date,
                weekday,
                shifts: dayShifts
            });
        }

        // Przygotuj dane w formacie wymaganym przez PrintableSchedule
        return {
            employee: {
                ...userData,
                uid: userId
            },
            year,
            month,
            days
        };
    } catch (error) {
        console.error('Error getting employee month details:', error);
        throw error;
    }
};

export const approveAllMonthSchedules = async (userId, year, month) => {
    try {
        const startDate = `${year}-${String(month).padStart(2, '0')}-01`;
        const endDate = `${year}-${String(month).padStart(2, '0')}-${String(new Date(year, month, 0).getDate()).padStart(2, '0')}`;

        const schedulesRef = collection(db, 'schedules');
        const q = query(schedulesRef,
            where('user_id', '==', userId),
            where('date', '>=', startDate),
            where('date', '<=', endDate),
            where('status', '==', 'pending')
        );

        const snapshot = await getDocs(q);
        const batch = [];

        snapshot.docs.forEach(doc => {
            batch.push(updateDoc(doc.ref, {
                status: 'approved',
                updatedAt: new Date().toISOString()
            }));
        });

        await Promise.all(batch);
        return { count: batch.length };
    } catch (error) {
        console.error('Error approving all month schedules:', error);
        throw error;
    }
};
